import type { GraphEdge, GraphNode } from '../../../types'
import { formatPercentRange } from '../../../utils/format'

/**
 * »Kæden i ord« (DESIGN.md → Ejerskabsgrafen): the ownership chain between
 * a selected graph node and the company itself as one Danish sentence that
 * a journalist can paste into an article.
 *
 *   »Henrik Aaskov Lind ejer 100 % af Lind Invest ApS, som ejer 66,67–89,99 %
 *    af Nordlys Holding ApS, som ejer 50–66,66 % af Nordlys Rederi ApS.«
 */

/**
 * Shortest chain of legal ownership edges between `nodeId` and `rootId`,
 * in owner → owned order. Works for owners above the root and for
 * subsidiaries below it. Null when the two are not connected.
 */
export function ownershipChainPath(
  nodeId: string,
  rootId: string,
  edges: GraphEdge[],
): GraphEdge[] | null {
  if (nodeId === rootId) return null
  const legal = edges.filter((e) => e.kind === 'legal')
  return shortestOwnerPath(nodeId, rootId, legal) ?? shortestOwnerPath(rootId, nodeId, legal)
}

function shortestOwnerPath(fromId: string, toId: string, edges: GraphEdge[]): GraphEdge[] | null {
  const outgoing = new Map<string, GraphEdge[]>()
  for (const e of edges) {
    const list = outgoing.get(e.from)
    if (list) list.push(e)
    else outgoing.set(e.from, [e])
  }
  const via = new Map<string, GraphEdge>()
  const seen = new Set([fromId])
  const queue = [fromId]
  while (queue.length > 0) {
    const current = queue.shift()!
    if (current === toId) break
    for (const e of outgoing.get(current) ?? []) {
      if (seen.has(e.to)) continue
      seen.add(e.to)
      via.set(e.to, e)
      queue.push(e.to)
    }
  }
  if (!via.has(toId)) return null
  const path: GraphEdge[] = []
  let cursor = toId
  while (cursor !== fromId) {
    const e = via.get(cursor)!
    path.unshift(e)
    cursor = e.from
  }
  return path
}

/** CVR interval as written in prose: »66,67–89,99 %« (one % sign). */
function intervalText(edge: GraphEdge): string | null {
  const pct = formatPercentRange(edge.capitalPercent, edge.capitalPercentMax)
  return pct ? pct.replace(' %–', '–') : null
}

function stakeText(edge: GraphEdge): string {
  const pct = intervalText(edge)
  return pct ? `${pct} af` : 'en andel af'
}

/** The chain as a sentence, or null when there is nothing to say. */
export function ownershipChainSentence(
  nodeId: string,
  rootId: string,
  nodes: GraphNode[],
  edges: GraphEdge[],
): string | null {
  const names = new Map(nodes.map((n) => [n.id, n.navn?.trim() || 'ukendt']))
  const name = (id: string) => names.get(id) ?? 'ukendt'

  const path = ownershipChainPath(nodeId, rootId, edges)
  if (path && path.length > 0) {
    const [first, ...rest] = path
    let sentence = `${name(first.from)} ejer ${stakeText(first)} ${name(first.to)}`
    for (const e of rest) {
      sentence += `, som ejer ${stakeText(e)} ${name(e.to)}`
    }
    return `${sentence}.`
  }

  const beneficial = edges.find((e) => e.kind === 'beneficial' && e.from === nodeId && e.to === rootId)
  if (beneficial) {
    const pct = intervalText(beneficial)
    return pct
      ? `${name(nodeId)} er reel ejer af ${name(rootId)} med ${pct}.`
      : `${name(nodeId)} er reel ejer af ${name(rootId)}.`
  }
  return null
}
